import {
  DASHBOARD_RESOLVE_CONTENT_TYPE,
  DashboardEdit,
  type DashboardResolveRequest,
} from "@t3tools/contracts";
import {
  experimental_streamedQuery as streamedQuery,
  keepPreviousData,
  queryOptions,
} from "@tanstack/react-query";
import { Schema } from "effect";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../../operatorsAuthSession";
import {
  EMPTY_ARTIFACT_RESOLVE,
  readArtifactResolveStream,
  reduceArtifactResolve,
  type ArtifactResolveData,
  type ArtifactResolveEvent,
} from "./artifactResolve";
import {
  decodeDashboardAssistantTimeline,
  type DashboardAssistantTimelineItem,
} from "./assistant/dashboardAssistantTimeline";
import {
  decodeDashboardArtifactChange,
  decodeDashboardArtifactDetail,
  decodeDashboardArtifactEditResult,
  decodeDashboardArtifactList,
  decodeDashboardArtifactOptions,
  decodeDashboardArtifactVersions,
  type DashboardArtifactDocument,
  type DashboardArtifactEditResult,
  type DashboardArtifactOptionsResponse,
  type DashboardArtifactSummary,
  type DashboardArtifactVersionSummary,
} from "./artifactTypes";

/** A parameter's options dataset, run with the date ranges it binds. */
export type ArtifactOptionsRequest = {
  datasetId: string;
  versionId: string;
  parameters: Readonly<Record<string, unknown>>;
};

/** Loads a parameter's options. Injectable so stories and tests can serve fixture rows. */
export type ArtifactOptionsFetcher = (
  request: ArtifactOptionsRequest,
) => Promise<DashboardArtifactOptionsResponse>;

/**
 * Opens a resolve stream. Injectable so stories and tests can replay fixture
 * lines; the response body is NDJSON and should stop when `signal` aborts.
 */
export type ArtifactResolveTransport = (input: {
  ref: string;
  request: DashboardResolveRequest;
  signal: AbortSignal;
}) => Promise<Response>;

/** A failed artifact request, carrying the server's explanation. */
export class DashboardArtifactRequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "DashboardArtifactRequestError";
    this.status = status;
  }

  /** Timeouts and paused datasets can recover; bad SQL or parameters cannot. */
  get retryable(): boolean {
    return this.status === 503 || this.status === 504;
  }
}

export const dashboardArtifactsQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "artifacts"] as const,
  list: () => [...dashboardArtifactsQueryKeys.all, "list"] as const,
  trash: () => [...dashboardArtifactsQueryKeys.all, "trash"] as const,
  versions: (ref: string) => [...dashboardArtifactsQueryKeys.all, "versions", ref] as const,
  assistantThread: (ref: string) =>
    [...dashboardArtifactsQueryKeys.all, "assistant-thread", ref] as const,
  detail: (ref: string, version: number | undefined) =>
    [...dashboardArtifactsQueryKeys.all, "detail", ref, version ?? null] as const,
  options: (ref: string, request: ArtifactOptionsRequest) =>
    [
      ...dashboardArtifactsQueryKeys.all,
      "options",
      ref,
      request.versionId,
      request.datasetId,
      request.parameters,
    ] as const,
  resolve: (ref: string, request: DashboardResolveRequest) =>
    [
      ...dashboardArtifactsQueryKeys.all,
      "resolve",
      ref,
      request.versionId ?? null,
      request.parameters,
    ] as const,
};

const ARTIFACTS_PATH = "/api/v1/dashboards/artifacts";

async function throwForResponse(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status === 401) throwOperatorsAuthExpired();
  if (response.ok) return;
  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  if (response.status === 403) {
    throw new OperatorsForbiddenError(message || "You do not have access to Dashboards.");
  }
  throw new DashboardArtifactRequestError(response.status, message || fallbackMessage);
}

function artifactUrl(ref: string, action?: string): string {
  return `${getOperatorsHttpOrigin()}${ARTIFACTS_PATH}/${encodeURIComponent(ref)}${action ? `/${action}` : ""}`;
}

export async function fetchDashboardArtifact(
  ref: string,
  version?: number,
): Promise<DashboardArtifactDocument> {
  const search =
    version === undefined ? "" : `?${new URLSearchParams({ version: String(version) })}`;
  const response = await fetch(`${artifactUrl(ref)}${search}`, { credentials: "include" });
  await throwForResponse(response, "Failed to load dashboard");
  return decodeDashboardArtifactDetail(await response.json());
}

/**
 * Rows of a parameter's options dataset. The resolve stream carries widgets
 * only, so this is the last reader of `POST :ref/data`.
 */
export async function fetchDashboardArtifactOptions(
  ref: string,
  request: ArtifactOptionsRequest,
): Promise<DashboardArtifactOptionsResponse> {
  const response = await fetch(artifactUrl(ref, "data"), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwForResponse(response, "Failed to load filter options");
  return decodeDashboardArtifactOptions(await response.json());
}

/** `POST :ref/resolve`: one NDJSON stream for every data widget. */
export const fetchDashboardArtifactResolve: ArtifactResolveTransport = async (input) => {
  const response = await fetch(artifactUrl(input.ref, "resolve"), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", accept: DASHBOARD_RESOLVE_CONTENT_TYPE },
    body: JSON.stringify(input.request),
    signal: input.signal,
  });
  await throwForResponse(response, "Failed to load dashboard data");
  return response;
};

export async function fetchDashboardArtifacts(
  options: { deleted?: boolean } = {},
): Promise<ReadonlyArray<DashboardArtifactSummary>> {
  const query = options.deleted ? "?deleted=true" : "";
  const response = await fetch(`${getOperatorsHttpOrigin()}${ARTIFACTS_PATH}${query}`, {
    credentials: "include",
  });
  await throwForResponse(response, "Failed to load dashboards");
  return decodeDashboardArtifactList(await response.json());
}

export async function fetchDashboardArtifactVersions(
  ref: string,
): Promise<ReadonlyArray<DashboardArtifactVersionSummary>> {
  const response = await fetch(artifactUrl(ref, "versions"), { credentials: "include" });
  await throwForResponse(response, "Failed to load versions");
  return decodeDashboardArtifactVersions(await response.json());
}

/** This person's saved conversation with the assistant about the dashboard. */
export async function fetchDashboardAssistantThread(
  ref: string,
): Promise<ReadonlyArray<DashboardAssistantTimelineItem>> {
  const response = await fetch(artifactUrl(ref, "assistant-thread"), { credentials: "include" });
  await throwForResponse(response, "Failed to load the conversation");
  return decodeDashboardAssistantTimeline(await response.json());
}

export async function deleteDashboardArtifact(ref: string): Promise<void> {
  const response = await fetch(artifactUrl(ref, "delete"), {
    method: "POST",
    credentials: "include",
  });
  await throwForResponse(response, "Failed to delete dashboard");
}

export async function restoreDashboardArtifact(ref: string): Promise<void> {
  const response = await fetch(artifactUrl(ref, "restore"), {
    method: "POST",
    credentials: "include",
  });
  await throwForResponse(response, "Failed to restore dashboard");
}

export type DashboardArtifactChange = "publish" | "rollback";

/** Publish or roll back moves what viewers see. */
export async function changeDashboardArtifact(
  ref: string,
  change: DashboardArtifactChange,
  versionId?: string,
): Promise<DashboardArtifactSummary> {
  const response = await fetch(artifactUrl(ref, change), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(versionId === undefined ? {} : { versionId }),
  });
  await throwForResponse(response, `Failed to ${change} dashboard`);
  return decodeDashboardArtifactChange(await response.json());
}

const encodeDashboardEdit = Schema.encodeSync(DashboardEdit);

/**
 * Saves `edit.ops` as a new version made from `edit.baseVersion`
 * (`POST /api/v1/dashboards/artifacts/:ref/edits`). A stale base is rejected
 * with a 409 `DashboardArtifactRequestError`, never merged.
 */
export async function postDashboardArtifactEdits(
  ref: string,
  edit: DashboardEdit,
): Promise<DashboardArtifactEditResult> {
  const response = await fetch(
    `${getOperatorsHttpOrigin()}${ARTIFACTS_PATH}/${encodeURIComponent(ref)}/edits`,
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(encodeDashboardEdit(edit)),
    },
  );
  await throwForResponse(response, "Failed to save the changes");
  return decodeDashboardArtifactEditResult(await response.json());
}

export function dashboardArtifactsListQueryOptions() {
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.list(),
    queryFn: () => fetchDashboardArtifacts(),
  });
}

/** What this person deleted and may restore. */
export function dashboardArtifactsTrashQueryOptions() {
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.trash(),
    queryFn: () => fetchDashboardArtifacts({ deleted: true }),
  });
}

export function dashboardAssistantThreadQueryOptions(ref: string) {
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.assistantThread(ref),
    queryFn: () => fetchDashboardAssistantThread(ref),
    // The live thread owns what happens after the page opens; this is only the past.
    staleTime: Infinity,
    gcTime: 0,
  });
}

export function dashboardArtifactVersionsQueryOptions(ref: string) {
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.versions(ref),
    queryFn: () => fetchDashboardArtifactVersions(ref),
  });
}

export function dashboardArtifactQueryOptions(ref: string, version?: number) {
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.detail(ref, version),
    queryFn: () => fetchDashboardArtifact(ref, version),
  });
}

const retryOnce = (failureCount: number, error: Error) =>
  error instanceof DashboardArtifactRequestError && error.retryable && failureCount < 1;

export function dashboardArtifactOptionsQueryOptions(input: {
  ref: string;
  request: ArtifactOptionsRequest;
  fetchOptions?: ArtifactOptionsFetcher | undefined;
}) {
  const fetchOptions =
    input.fetchOptions ?? ((request) => fetchDashboardArtifactOptions(input.ref, request));
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.options(input.ref, input.request),
    queryFn: () => fetchOptions(input.request),
    placeholderData: keepPreviousData,
    retry: retryOnce,
  });
}

/**
 * The whole dashboard as one streamed query: data grows line by line while
 * the stream is open. A new filter is a new key, so React Query aborts the
 * old stream (its signal is consumed) and starts the new one. A refetch of
 * the same key keeps the old lines until the new stream finishes.
 */
export function dashboardArtifactResolveQueryOptions(input: {
  ref: string;
  request: DashboardResolveRequest;
  transport?: ArtifactResolveTransport | undefined;
}) {
  const transport = input.transport ?? fetchDashboardArtifactResolve;
  return queryOptions({
    queryKey: dashboardArtifactsQueryKeys.resolve(input.ref, input.request),
    queryFn: streamedQuery<ArtifactResolveEvent, ArtifactResolveData>({
      streamFn: async function* ({ signal }) {
        const response = await transport({ ref: input.ref, request: input.request, signal });
        yield* readArtifactResolveStream(response, signal);
      },
      reducer: reduceArtifactResolve,
      initialValue: EMPTY_ARTIFACT_RESOLVE,
      refetchMode: "replace",
    }),
    retry: retryOnce,
  });
}
